import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import { useUserSession } from '@/context/user-session-context';
import { useUser } from '@/firebase';
import { ConditionalAppShell } from '@/components/layout/conditional-app-shell';

const PENDING_PATH = '/account-pending-deletion';
const RECOVERY_PATH = '/restore-account';

export function AccountStatusGate({ children }: { children: React.ReactNode }) {
  const { user: authUser, isUserLoading } = useUser();
  const { user, loading } = useUserSession();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const pending = user?.accountStatus === 'pending_deletion';
  const recoveryRoute = pathname === RECOVERY_PATH;

  useEffect(() => {
    if (isUserLoading || (authUser && loading)) return;
    if (pending && pathname !== PENDING_PATH && !recoveryRoute) {
      void navigate({ to: PENDING_PATH, replace: true });
    } else if (authUser && !pending && pathname === PENDING_PATH) {
      void navigate({ to: '/', replace: true });
    }
  }, [authUser, isUserLoading, loading, navigate, pathname, pending, recoveryRoute]);

  const redirectingPending = pending && pathname !== PENDING_PATH && !recoveryRoute;
  const redirectingActive = Boolean(authUser && !pending && pathname === PENDING_PATH);
  if (isUserLoading || (authUser && loading) || redirectingPending || redirectingActive) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Do not mount novel/subscription providers for a locked account. The only
  // rendered application surface is the pending page (plus token recovery).
  if (pending) {
    return (
      <ConditionalAppShell>
        <Outlet />
      </ConditionalAppShell>
    );
  }

  return <>{children}</>;
}
